<script setup lang="ts">
import { computed } from 'vue'
import { useAskSliceyStore } from '@modules/askSlicey'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'

const store = useAskSliceyStore()

// Hide while the drawer is open — the FAB stops being a useful affordance
// once Slicey itself is on-screen.
const isVisible = computed(() => !store.drawerOpen)

const handleClick = () => store.openDrawer()
</script>

<template>
  <Box
    v-if="isVisible"
    is="button"
    align="center"
    gap="2"
    class="ask-fab"
    aria-label="Open Slicey — your portfolio copilot"
    @click="handleClick"
  >
    <Box class="ask-fab__icon" align="center" justify="center">
      <PiIcon name="sparkles" />
    </Box>
    <Text type="body" weight="semibold" color="inverse" is="span" class="ask-fab__label">
      Slicey
    </Text>
  </Box>
</template>

<style scoped>
.ask-fab {
  position: fixed;
  bottom: max(1.5rem, env(safe-area-inset-bottom));
  right: 1.5rem;
  z-index: 30;
  padding: 0.75rem 1.125rem 0.75rem 0.625rem;
  border-radius: 999px;
  border: none;
  background: var(--p-primary-600);
  color: var(--p-surface-0);
  cursor: pointer;
  box-shadow:
    0 8px 20px rgba(103, 85, 251, 0.28),
    0 2px 6px rgba(16, 24, 40, 0.08);
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease,
    background 0.15s ease;
  min-height: 48px;
}

.ask-fab:hover {
  background: var(--p-primary-700);
  transform: translateY(-1px);
  box-shadow:
    0 12px 24px rgba(103, 85, 251, 0.32),
    0 4px 10px rgba(16, 24, 40, 0.1);
}

.ask-fab:focus-visible {
  outline: 2px solid var(--p-primary-300);
  outline-offset: 3px;
}

.ask-fab__icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  font-size: 0.95rem;
  animation: ask-fab-pulse 2.4s ease-in-out infinite;
}

@keyframes ask-fab-pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.45);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(255, 255, 255, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ask-fab__icon {
    animation: none;
  }
}

@media (max-width: 640px) {
  .ask-fab {
    right: 1rem;
    padding: 0.625rem;
    min-height: 52px;
    min-width: 52px;
  }

  .ask-fab__label {
    display: none;
  }
}
</style>
